import { Link } from "react-router-dom";
import { SISTER_SITES } from "@/lib/sisterSites";
import { PROGRAM_LIST } from "@/lib/programs";
import Seo from "@/components/Seo";
import { ArrowRight, ExternalLink, Cross } from "lucide-react";

export default function Family() {
  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <Seo
        title="The Jesus Just Us family · Sister ministries & marketplace"
        description="Big Deal Big Things, Confidence Arise, Million Dollar Mindset, and the wider family of Kingdom properties under Jesus Just Us. One faith. One family. One mission."
        path="/family"
      />

      <div className="overline text-burgundy flex items-center gap-2">
        <Cross className="h-3.5 w-3.5" /> The family
      </div>
      <h1 className="font-serif text-5xl text-jjblue mt-3 leading-tight">
        More than a community.<br />
        <span className="text-burgundy italic">A Kingdom family.</span>
      </h1>
      <p className="mt-6 max-w-2xl text-[var(--jj-text-secondary)] leading-relaxed text-lg">
        Jesus Just Us is the gathering place — but the work doesn't stop here. These are the sister
        properties and native programs built alongside JJU, each rooted in the same mission: building
        up the body of Christ, in real and tangible ways.
      </p>

      <div className="overline text-burgundy mt-14">Sister properties</div>
      <h2 className="font-serif text-3xl text-jjblue mt-2 leading-tight">Outside the gates of JJU</h2>
      <p className="mt-2 text-sm text-[var(--jj-muted)]">External sites — they open in a new tab.</p>
      <div className="mt-6 space-y-6">
        {SISTER_SITES.map((s) => {
          const Icon = s.icon;
          const accentRing = s.accent === "gold" ? "ring-[var(--jj-gold)]/60" : "ring-[var(--jj-burgundy)]/40";
          const accentBg = s.accent === "gold" ? "bg-gold/15 text-burgundy" : "bg-burgundy/10 text-burgundy";
          return (
            <div
              key={s.key}
              className={`jj-card p-8 sm:p-10 ring-1 ${accentRing} hover:-translate-y-0.5 transition-transform duration-300`}
              data-testid={`family-${s.key}`}
            >
              <div className="flex items-start gap-5 flex-wrap">
                <div className={`h-14 w-14 rounded-2xl ${accentBg} flex items-center justify-center shrink-0`}>
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-[260px]">
                  <div className="overline text-burgundy" data-testid={`family-${s.key}-badge`}>{s.badge}</div>
                  <h3 className="font-serif text-3xl text-jjblue mt-1 leading-tight">{s.name}</h3>
                  <p className="mt-1 text-sm text-[var(--jj-muted)] italic">{s.tagline}</p>
                  <p className="mt-4 text-[var(--jj-text-secondary)] leading-relaxed">{s.longBlurb}</p>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary mt-6 inline-flex items-center gap-2"
                    data-testid={`family-${s.key}-cta`}
                  >
                    {s.cta} <ExternalLink className="h-4 w-4" strokeWidth={1.7} />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="overline text-burgundy mt-16">Native programs</div>
      <h2 className="font-serif text-3xl text-jjblue mt-2 leading-tight">Built right inside JJU</h2>
      <p className="mt-2 text-sm text-[var(--jj-muted)]">Read about each, then express interest from the program page.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-6">
        {PROGRAM_LIST.map((p) => {
          const accentRing = p.accent === "gold" ? "ring-[var(--jj-gold)]/60 hover:ring-[var(--jj-gold)]" : "ring-[var(--jj-burgundy)]/40 hover:ring-[var(--jj-burgundy)]";
          const accentBg = p.accent === "gold" ? "bg-gold/15 text-burgundy" : "bg-burgundy/10 text-burgundy";
          return (
            <Link
              key={p.key}
              to={`/${p.key}`}
              className={`jj-card p-7 ring-1 ${accentRing} transition-all duration-300 hover:-translate-y-1 flex flex-col`}
              data-testid={`family-program-${p.key}`}
            >
              <div className="flex items-center gap-3">
                <span className={`h-11 w-11 rounded-2xl ${accentBg} flex items-center justify-center`}>
                  <Cross className="h-4 w-4" strokeWidth={1.6} />
                </span>
                <div className="overline text-burgundy">Program</div>
              </div>
              <h3 className="font-serif text-2xl text-jjblue mt-5 leading-tight">{p.name}</h3>
              <p className="mt-1 text-sm text-burgundy/85 italic">{p.tagline}</p>
              <p className="mt-3 text-[var(--jj-text-secondary)] leading-relaxed flex-1">{p.heroBlurb}</p>
              <span className="mt-5 text-burgundy font-medium text-sm inline-flex items-center gap-1.5">
                Explore the program <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          );
        })}
      </div>

      <div className="mt-16 jj-card p-8 text-center bg-gradient-to-br from-cream to-cream-surface">
        <div className="overline text-burgundy">Belong here first</div>
        <h2 className="font-serif text-3xl text-jjblue mt-2">Your home base is Jesus Just Us.</h2>
        <p className="mt-3 text-[var(--jj-text-secondary)] max-w-xl mx-auto">
          The marketplace, the programs, the prayer circles — they all flow from one place. Make your
          home here, and the rest of the family is one click away.
        </p>
        <Link to="/feed" className="btn-gold mt-7 inline-flex items-center gap-2" data-testid="family-back-to-feed">
          Open the feed <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
